import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  GlobeAltIcon,
  TagIcon,
  ShieldExclamationIcon,
  ArrowTopRightOnSquareIcon,
  BoltIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import threatIntelligenceService from '@/services/threat-intelligence.service';

const threatColors = {
  low: 'bg-success-100 text-success-800 border-success-200 dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
  medium: 'bg-warning-100 text-warning-800 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
  high: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800',
  critical: 'bg-danger-100 text-danger-800 border-danger-200 dark:bg-danger-900/20 dark:text-danger-300 dark:border-danger-800',
};

const reliabilityColors: Record<string, string> = {
  A: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  B: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
  C: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  D: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
  E: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  F: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
};

const reliabilityLabels: Record<string, string> = {
  A: 'Highly Reliable',
  B: 'Usually Reliable',
  C: 'Fairly Reliable',
  D: 'Not Usually Reliable',
  E: 'Unreliable',
  F: 'Cannot Be Judged',
};

export default function ThreatFeedItemDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: feedItem, isLoading, error } = useQuery({
    queryKey: ['feed-item', id],
    queryFn: () => {
      if (!id) {
        throw new Error('No feed item ID provided');
      }
      return threatIntelligenceService.getFeedItemById(id);
    },
    enabled: !!id,
  });

  const handleBack = () => {
    navigate('/security/threats', { state: { activeTab: 'feed' } });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Strip HTML tags for plain text display, but preserve the content
  const stripHtml = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent ?? '';
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            <p className="text-sm text-secondary-600 dark:text-secondary-400">Loading feed item...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error ?? !feedItem) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-12">
            <div className="text-center">
              <ExclamationTriangleIcon className="w-16 h-16 mx-auto text-danger-500 mb-4" />
              <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
                Feed Item Not Found
              </h2>
              <p className="text-secondary-600 dark:text-secondary-400 mb-6">
                The feed item you&apos;re looking for doesn&apos;t exist or has been removed.
              </p>
              <Button onClick={handleBack}>
                Back to Threat Intelligence
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Threat Feeds
          </Button>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                  <ShieldExclamationIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
                    {feedItem.title}
                  </h1>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                    From {feedItem.sourceName}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {feedItem.threatLevel && (
                  <span className={cn('px-3 py-1 text-xs font-medium rounded-full border', threatColors[feedItem.threatLevel])}>
                    {feedItem.threatLevel.toUpperCase()} THREAT
                  </span>
                )}
                {feedItem.category && (
                  <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full">
                    {feedItem.category.toUpperCase()}
                  </span>
                )}
                {feedItem.sourceReliability && (
                  <span className={cn('px-3 py-1 text-xs font-medium rounded-full', reliabilityColors[feedItem.sourceReliability])}>
                    Reliability: {feedItem.sourceReliability}
                  </span>
                )}
              </div>
            </div>

            {feedItem.link && (
              <Button
                variant="outline"
                onClick={() => window.open(feedItem.link, '_blank', 'noopener,noreferrer')}
                className="flex-shrink-0 ml-4"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-2" />
                View Original Article
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {feedItem.description ? (
                  <p className="text-secondary-700 dark:text-secondary-300 leading-relaxed whitespace-pre-wrap">
                    {stripHtml(feedItem.description)}
                  </p>
                ) : (
                  <p className="text-secondary-500 dark:text-secondary-400 italic">
                    No description available.
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Indicators Card */}
          {feedItem.indicators && feedItem.indicators.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Indicators</CardTitle>
                      <CardDescription>
                        Key indicators identified in this feed item
                      </CardDescription>
                    </div>
                    <BoltIcon className="w-5 h-5 text-warning-600 dark:text-warning-400" />
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-2">
                    {feedItem.indicators.map((indicator, index) => (
                      <div
                        key={index}
                        className="flex items-center p-3 bg-secondary-50 dark:bg-secondary-800 rounded-lg"
                      >
                        <div className="w-2 h-2 bg-warning-500 rounded-full mr-3 flex-shrink-0" />
                        <code className="text-sm text-secondary-900 dark:text-white font-mono break-all">
                          {indicator}
                        </code>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Affected Assets Card */}
          {feedItem.affectedAssets && feedItem.affectedAssets.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Affected Assets</CardTitle>
                      <CardDescription>
                        {feedItem.affectedAssets.length} {feedItem.affectedAssets.length === 1 ? 'asset' : 'assets'} potentially impacted
                      </CardDescription>
                    </div>
                    <BuildingOfficeIcon className="w-5 h-5 text-danger-600 dark:text-danger-400" />
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex flex-wrap gap-2">
                    {feedItem.affectedAssets.map((asset, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 text-xs font-medium bg-danger-50 text-danger-700 dark:bg-danger-900/20 dark:text-danger-300 rounded-lg border border-danger-200 dark:border-danger-800"
                      >
                        {asset}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Tags Card */}
          {feedItem.tags && feedItem.tags.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Tags</CardTitle>
                    <TagIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex flex-wrap gap-2">
                    {feedItem.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 text-xs font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 rounded-lg"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Sidebar Column */}
        <div className="lg:col-span-1 space-y-6">
          {/* Source Details Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Source Details</CardTitle>
                  <GlobeAltIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Source Name */}
                  <div>
                    <div className="text-xs font-medium uppercase text-secondary-600 dark:text-secondary-400 mb-1">
                      Source
                    </div>
                    <p className="text-sm font-medium text-secondary-900 dark:text-white">
                      {feedItem.sourceName}
                    </p>
                  </div>

                  {/* Source Type */}
                  <div>
                    <div className="text-xs font-medium uppercase text-secondary-600 dark:text-secondary-400 mb-1">
                      Type
                    </div>
                    <p className="text-sm text-secondary-900 dark:text-white capitalize">
                      {feedItem.sourceType}
                    </p>
                  </div>

                  {/* Reliability */}
                  {feedItem.sourceReliability && (
                    <div>
                      <div className="text-xs font-medium uppercase text-secondary-600 dark:text-secondary-400 mb-1">
                        Reliability Rating
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={cn('px-2 py-0.5 text-xs font-semibold rounded', reliabilityColors[feedItem.sourceReliability])}>
                          {feedItem.sourceReliability}
                        </span>
                        <span className="text-sm text-secondary-700 dark:text-secondary-300">
                          {reliabilityLabels[feedItem.sourceReliability] ?? feedItem.sourceReliability}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Source Category */}
                  {feedItem.sourceCategory && (
                    <div>
                      <div className="text-xs font-medium uppercase text-secondary-600 dark:text-secondary-400 mb-1">
                        Category
                      </div>
                      <p className="text-sm text-secondary-900 dark:text-white capitalize">
                        {feedItem.sourceCategory.replace(/_/g, ' ')}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Timestamps Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Timeline</CardTitle>
                  <ClockIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Published Date */}
                  <div>
                    <div className="text-xs font-medium uppercase text-secondary-600 dark:text-secondary-400 mb-1">
                      Published
                    </div>
                    <p className="text-sm text-secondary-900 dark:text-white">
                      {formatDate(feedItem.publishedDate)}
                    </p>
                  </div>

                  {/* Imported Date */}
                  <div>
                    <div className="text-xs font-medium uppercase text-secondary-600 dark:text-secondary-400 mb-1">
                      Imported
                    </div>
                    <p className="text-sm text-secondary-900 dark:text-white">
                      {formatDateShort(feedItem.createdAt)}
                    </p>
                  </div>

                  {/* Processed Status */}
                  <div>
                    <div className="text-xs font-medium uppercase text-secondary-600 dark:text-secondary-400 mb-1">
                      Processing Status
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={cn(
                        'px-2 py-0.5 text-xs font-medium rounded-full',
                        feedItem.processed
                          ? 'bg-success-100 text-success-700 dark:bg-success-900/20 dark:text-success-300'
                          : 'bg-warning-100 text-warning-700 dark:bg-warning-900/20 dark:text-warning-300'
                      )}>
                        {feedItem.processed ? 'Processed' : 'Pending'}
                      </span>
                      {feedItem.processedAt && (
                        <span className="text-xs text-secondary-500 dark:text-secondary-400">
                          {formatDateShort(feedItem.processedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
